import { onViewChange, getCurrentView } from '@utils/breakpoints';
import { pubSub } from '@utils/PubSub';
import { loadCart, awaitWindowVariable, getFirstFocusableElement, getLastFocusableElement, trapFocus } from '@utils/global-functions';
import { topNavLi, drawerWrapper, level4Item, level3Item, level3ItemContainer, level2ItemContainer, level2Item, footerLi, exposedNavLink } from '@utils/NavResources';

export default class Navigation {
  constructor() {
    document.querySelector('#shopify-section-header').setAttribute('data-open', true);
    this.initialScrollListeners();
    this.triggerDY();
    this.lastScrollTop = 0;
    pubSub.subscribe('dy:nav:init', (e) => {
      let navObj;
      if (typeof e.detail?.navigationJSON == 'object') navObj = e.detail.navigationJSON;
      else if (typeof e.detail?.navigationJSON == 'string') navObj = JSON.parse(e.detail.navigationJSON);
      else navObj = null;

      this.initNav(navObj);
    });

    setTimeout(() => {
      if (!window.Brooklinen.navLoaded) {
        this.initNav();
      }
      pubSub.publish('nav:loaded');
    }, window.Brooklinen.dyExpiration || 5000);
  }

  //scroll listeners on nav load
  initialScrollListeners() {
    this.scrollListeners();
    //wait to add the sticky property to the header in order to get a smoother transition effect
    setTimeout(() => {
      document.querySelector('#shopify-section-header').setAttribute('data-sticky', true);
    }, 800);
    onViewChange(() => this.scrollListeners(), 'med', 'max');
    document.addEventListener('scroll', () => {
      this.scrollListeners();
    });
  }
  //picks whether to use fallback JSON or DY JSON, then fires function to check if nano has loaded
  initNav(obj) {
    if (window.Brooklinen?.nav && typeof window.Brooklinen?.nav != 'object') {
      window.Brooklinen.nav = JSON.parse(window.Brooklinen.nav);
    } else if (!window.Brooklinen.nav) {
      window.Brooklinen.nav = obj || JSON.parse(document.querySelector('#navigation_fallback_data').textContent);
    }

    this.awaitNano();
    this.setNavOffset();
    window.addEventListener('resize', this.setNavOffset.bind(this));
    window.addEventListener('scroll', this.setNavOffset.bind(this));
  }
  //this lets DY know that we're loaded, and in turn DY will populate the window JSON and fire dy:nav:init
  triggerDY() {
    window.Brooklinen.DOMContentLoaded = () => {
      return true;
    };
  }
  //this lets the nav know when the nano has finally finished populating its content, so we can fire loadHeader
  awaitNano() {
    if (!window.Brooklinen.navLoaded) {
      awaitWindowVariable(window.Brooklinen, 'nanoLoaded');
      this.loadHeader();
    }
  }
  setNavOffset() {
    const header = document.getElementById('shopify-section-header');
    const nanoOffset = header.style?.top.endsWith('px') ? parseFloat(header.style.top) : 0;
    const headerOffset = `${header.offsetHeight + nanoOffset}px`;

    document.documentElement.style.setProperty('--navOffset', headerOffset);
  }
  //creates the desktop and mobile L1 links
  createTopnavElements(data) {
    for (let i in data) {
      const drawerId = data[i].label.replaceAll(' ', ''),
        isDrawerLink = window.Brooklinen.nav[drawerId];
      document.querySelector('.header__inline-menu > .list-menu').innerHTML += topNavLi(drawerId, isDrawerLink, data[i]);
      document.querySelector('content-drawer[data-label="mobile-nav"] ul.level-1__item').innerHTML += topNavLi(drawerId, isDrawerLink, data[i]);
    }
    return true;
  }

  //creates the L2/L3/L4 items for desktop and mobile, as well as the individual drawers that house these elements
  async createDrawerElements(key) {
    await this.createTopnavElements();

    const drawerData = window.Brooklinen.nav[key];

    //determine the max number of columns from the data
    const drawerColumns = drawerData.reduce((a, b) => (a.columnOrder > b.columnOrder ? a : b))?.columnOrder;

    //create desktop drawer
    document.querySelector('.top-drawer').innerHTML += drawerWrapper(key, drawerColumns);
    let drawerContentContainer = document.querySelector(`.top-drawer .drawer-wrapper[data-wrapper="${key}"]`);
    //create mobile drawer
    document.querySelector('content-drawer[data-label="mobile-nav"] .mobile-nav').innerHTML += drawerWrapper(key, drawerColumns);

    //if there is a mobile drawer and its L1 parent has a link, remove the link from the L1 parent so clicking on it does not redirect you elsewhere
    document.querySelector(`content-drawer[data-label="mobile-nav"] .level-1__item a[data-drawer="${key}"]`).setAttribute('href', '#');
    const buttonEl = document.querySelector(`content-drawer[data-label="mobile-nav"] .level-1__item a[data-drawer="${key}"]`).outerHTML;
    document.querySelector(`content-drawer[data-label="mobile-nav"] .level-1__item a[data-drawer="${key}"]`).outerHTML = buttonEl.replace(/<a[^>]*>/g, `<button data-ht-element="nav-l1-link" class="header__menu-item list-menu__item link" data-drawer="${key}">`).replace(/<\/a>/g, '</button>');
    let mobileContentContainer = document.querySelector(`content-drawer[data-label="mobile-nav"] .drawer-wrapper[data-wrapper="${key}"]`);

    //loop through columns of the navigation.
    //filter the nav data based on the specified column, then determine the nav module type of that column (i.e. L2, L3, SML Tile, LRG Tile).
    //build the module for the specified column by looping through the relevant data.
    for (let i = 1; i <= drawerColumns; i++) {
      //get the current column we're building for
      const currentColumnData = drawerData.filter((line) => line.columnOrder == i);

      //get the current module we're building for the column
      const currentColumnType = currentColumnData?.[0].moduleType;

      //run the desktop and mobile HTML builder functions depending on the module type
      switch (true) {
        case currentColumnType == 'L3':
          const labelTitles = currentColumnData.filter((data) => !data.label);
          const l3Data = currentColumnData.filter((data) => data.label);

          //build the desktop and mobile versions of the L3 list containers
          for (let j in labelTitles) {
            const obj = {
              key: key,
              labelTitle: labelTitles[j].labelTitle,
              url: labelTitles[j].url,
            };
            drawerContentContainer.querySelector(`.drawer-wrapper__col-${i}`).innerHTML += level3ItemContainer(obj);

            //Appends the L3 module containers. Checks if there is a mobile override in the data where we want to insert the L3 module in the middle of an L2 module on mobile.
            //If a mobile override exists, append the L3 module in the indexed location within the L2 module, specified in the JSON data.
            //If a mobile override does not exist, the mobile nav simply follows the same stacking behavior.
            if (mobileContentContainer.querySelector(`.level-2__list`) && labelTitles[j].mobileL3PositionOverride) {
              if (!mobileContentContainer.querySelector('.pinned')) {
                const pinnedLocation = parseInt(labelTitles[j].mobileL3PositionOverride + 1);
                const l2PinnedEl = mobileContentContainer.querySelector(`.level-2__list .level-2__item:nth-child(${pinnedLocation})`);
                l2PinnedEl.classList.add('pinned');
              }
              mobileContentContainer.querySelector('.pinned').insertAdjacentHTML('beforebegin', level3ItemContainer(obj));
            } else {
              mobileContentContainer.querySelector(`.drawer-wrapper__col-${i}`).innerHTML += level3ItemContainer(obj);
            }
          }

          //Appends the desktop and mobile L3 items within the appropriate L3 containers.
          for (let j in l3Data) {
            const obj = {
              key: key,
              label: l3Data[j].label,
              url: l3Data[j].url,
              badge: l3Data[j].badge,
              caption: l3Data[j].caption,
            };
            const handleizedLabel = l3Data[j].labelTitle.toLowerCase().replace(/[^a-z0-9]+/g, '-');
            drawerContentContainer.querySelector(`.drawer-wrapper__col-${i} .list--${handleizedLabel}`).innerHTML += level3Item(obj);
            mobileContentContainer.querySelector(`.list--${handleizedLabel}`).innerHTML += level3Item(obj);
          }

          break;
        case currentColumnType == 'L2':
          //Appends the desktop and mobile L2 containers.
          drawerContentContainer.querySelector(`.drawer-wrapper__col-${i}`).innerHTML += level2ItemContainer(key);
          mobileContentContainer.querySelector(`.drawer-wrapper__col-${i}`).innerHTML += level2ItemContainer(key);

          //Appends the desktop and mobile L2 items within the appropriate L2 containers.
          for (let j in currentColumnData) {
            const obj = {
              key: key,
              label: currentColumnData[j].label,
              url: currentColumnData[j].url,
              badge: currentColumnData[j].badge,
            };
            drawerContentContainer.querySelector(`.drawer-wrapper__col-${i} .level-2__list`).innerHTML += level2Item(obj);

            mobileContentContainer.querySelector(`.drawer-wrapper__col-${i} .level-2__list`).innerHTML += level2Item(obj);
          }

          break;
        case currentColumnType.includes('Tile'):
          //Appends the small tile image + caption on desktop and mobile.
          for (let j in currentColumnData) {
            const obj = {
              key: key,
              url: currentColumnData[j].url,
              image: currentColumnData[j].tileImage,
              cta: currentColumnData[j].label,
              description: currentColumnData[j].caption,
              imageType: currentColumnType == 'SML Tile' ? 'sml' : 'lrg',
            };
            drawerContentContainer.querySelector(`.drawer-wrapper__col-${i}`).innerHTML += level4Item(obj);

            mobileContentContainer.innerHTML += level4Item(obj);
          }
          break;
        default:
          break;
      }
    }
  }

  //create the login / misc links in the mobile drawer
  createMobileFooterElements(data) {
    for (let i in data) {
      document.querySelector('content-drawer[data-label="mobile-nav"] .overlay__modal--content ul.mobile-nav--footer-links').innerHTML += footerLi(data[i].url, data[i].label);
    }
  }

  createExposedNavElement(data) {
    if (data.length > 0) {
      const exposedNav = document.createElement('div');
      exposedNav.classList.add('exposed-nav');
      exposedNav.setAttribute('data-ht-module', 'exposed-nav');
      for (let i in data) {
        exposedNav.innerHTML += exposedNavLink(data[i].label, data[i].url, i);
      }
      document.querySelector('#shopify-section-header').after(exposedNav);
    }
  }
  //iterate through the nav JSON and run the appropriate build functions depending on the key
  buildElements() {
    if (window.Brooklinen.nav) {
      for (let i in Object.keys(window.Brooklinen.nav)) {
        switch (Object.keys(window.Brooklinen.nav)[i]) {
          case 'topnav':
            this.createTopnavElements(window.Brooklinen.nav.topnav);
            break;
          case 'footernav':
            this.createMobileFooterElements(window.Brooklinen.nav.footernav);
            break;
          case 'exposednav':
            if (document.querySelector('main[data-template="index"]')) {
              this.createExposedNavElement(window.Brooklinen.nav.exposednav);
            }
            break;
          default:
            this.createDrawerElements(Object.keys(window.Brooklinen.nav)[i]);
            break;
        }
        this.setTabNavBehavior(Object.keys(window.Brooklinen.nav)[i]);
      }
    }
    return true;
  }

  async buildHeaderStyles(drawerId) {
    const setStyleProperties = () => {
      document.querySelector('.top-drawer').style.minHeight = document.querySelector('.top-drawer').offsetHeight + 'px';
      //remove the previously active drawer before showing the currently active drawer
      if (document.querySelector('.top-drawer div.active[data-wrapper]') && document.querySelector('.top-drawer div.active[data-wrapper]').getAttribute('data-wrapper') != drawerId) {
        document.querySelector('.top-drawer div.active[data-wrapper]').style.opacity = 0;
        document.querySelector('.top-drawer div.active[data-wrapper]').classList.remove('active');
      }

      //show the active drawer, and resize the drawer to fit the content
      document.querySelector(`.top-drawer div[data-wrapper="${drawerId}"]`).classList.add('active');
      document.querySelector('.top-drawer').style.minHeight = document.querySelector('.top-drawer div.active[data-wrapper]').offsetHeight + 'px';
      document.querySelector('.top-drawer').classList.add('active');

      return true;
    };

    await setStyleProperties();
  }

  //stores all event listeners pertaining to dynamically generated HTML nav elements
  initBuiltElementListeners() {
    //when hovering over a desktop L1 that does not have a drawer, close the currently active desktop drawer if applicable
    document.querySelectorAll('#shopify-section-header sticky-header li[data-drawer] a:not([data-drawer])').forEach((link) => {
      link.addEventListener('mouseover', () => {
        [document.querySelector('.navigation-overlay'), document.querySelector('.top-drawer')].forEach((el) => {
          el.classList.remove('active');
        });
      });
    });

    //when hovering over a desktop L1 that has a corresponding drawer, load the desktop drawer
    document.querySelectorAll('#shopify-section-header sticky-header a[data-drawer]').forEach((link) => {
      const drawerId = link.getAttribute('data-drawer');
      const events = ['mouseover', 'focus'];
      events.forEach((eventType) => {
        link.addEventListener(eventType, () => {
          document.querySelector('.navigation-overlay').classList.add('active');
          const value = this.buildHeaderStyles(drawerId);
          if (value) document.querySelector(`.top-drawer div[data-wrapper="${drawerId}"]`).style.opacity = 1;
        });
      });

      //on mobile L1 click, show the corresponding L2/3/4 content
      document.querySelector(`content-drawer[data-label="mobile-nav"] .level-1__item [data-drawer="${drawerId}"]`)?.addEventListener('click', () => {
        document.querySelector('content-drawer[data-label="mobile-nav"] .mobile-nav--l2-header').innerHTML = drawerId.replace(/([A-Z])/g, ' $1').trim();
        document.querySelector('content-drawer[data-label="mobile-nav"] .overlay__modal--content').classList.add('level-2--active');
        const mainDrawer = document.querySelector(`content-drawer[data-label="mobile-nav"]`);
        const l2Drawer = document.querySelector(`content-drawer[data-label="mobile-nav"] .drawer-wrapper[data-wrapper="${drawerId}"]`);
        const closeBtn = mainDrawer.querySelector('.overlay__modal--close') as HTMLButtonElement;
        const backBtn = mainDrawer.querySelector('.overlay__modal--back') as HTMLButtonElement;
        closeBtn.disabled = true;
        backBtn.disabled = false;
        l2Drawer.classList.add('active');
        trapFocus(mainDrawer, l2Drawer);
      });
    });

    //close the mobile drawer on click
    document.querySelector('content-drawer[data-label="mobile-nav"] .overlay__modal--close').addEventListener('click', (e) => {
      this.closeMobileDrawer();
    });

    //close the mobile drawer if user clicks outside the drawer
    document.querySelector('content-drawer[data-label="mobile-nav"] .overlay__modal--content').addEventListener('click', (e) => {
      const overlayBounds = document.documentElement.clientWidth - e.pageX;
      if (overlayBounds <= 40) this.closeMobileDrawer();
    });

    //close the desktop drawer if user hovers outside the drawer
    document.querySelector('.top-drawer').addEventListener('mouseleave', () => {
      [document.querySelector('.navigation-overlay'), document.querySelector('.top-drawer')].forEach((el) => {
        el.classList.remove('active');
      });
      document.querySelector('.top-drawer').style.minHeight = '';
    });

    //on mobile drawer, go back to L1 state from L2 on back button click
    document.querySelector('content-drawer[data-label="mobile-nav"] .mobile-nav--header .overlay__modal--back').addEventListener('click', () => {
      this.goBack();
    });

    //on mobile drawer L1 click, if L1 does not have an L2 drawer, go to the L1 link instead
    document.querySelectorAll('.mobile-nav [data-drawer-link]').forEach((link) => {
      link.addEventListener('click', (e) => {
        window.location.href = e.target.querySelector('a').getAttribute('href');
      });
    });

    //on mobile drawer logo click, close the drawer, then refresh page
    document.querySelector('.mobile-nav--header .header__heading-logo').addEventListener('click', () => {
      this.closeMobileDrawer();
      setTimeout(() => {
        location.reload();
      }, 1000);
    });

    //on mobile drawer cart icon click, close the drawer, then load the cart modal
    document.querySelector('.mobile-nav--header .header__icon--cart').addEventListener('click', (e) => {
      e.stopImmediatePropagation();
      this.closeMobileDrawer();
      loadCart();
    });
    //when resizing from tablet to desktop while the mobile drawer is still active, close the mobile drawer
    onViewChange(() => this.closeMobileDrawer(), '1100px', 'min');
    this.scrollListeners();
  }

  //closes the mobile drawer
  closeMobileDrawer() {
    const closeOverlayBackground = new CustomEvent('close-overlay', {
      detail: 'mobile-nav',
    });
    window.dispatchEvent(closeOverlayBackground);
  }

  //moves mobile drawer from L2 state back to L1
  goBack() {
    document.querySelector('content-drawer[data-label="mobile-nav"] .overlay__modal--content').classList.remove('level-2--active');
    document.querySelector('content-drawer[data-label="mobile-nav"] .drawer-wrapper.active').classList.remove('active');
    const mainDrawer = document.querySelector('content-drawer[data-label="mobile-nav"]');
    const closeBtn = mainDrawer.querySelector('.overlay__modal--close') as HTMLButtonElement;
    const backBtn = mainDrawer.querySelector('.overlay__modal--back') as HTMLButtonElement;
    closeBtn.disabled = false;
    backBtn.disabled = true;
    trapFocus(mainDrawer);
  }

  //determines vertical position on the page, and toggles "sticky" property accordingly. controls nano scrolling experience
  scrollListeners() {
    const mobileScrollThreshold = parseInt(document.querySelector('header').getAttribute('data-scroll-threshold') || 100);

    /**
     * If the user is viewing in a mobile screensize, the scrolling logic is as follows
     * * On any downscroll, detect if the a user has passed the mobile scroll threshold set in theme settings
     * * * If a user is past the scroll threshold, hide the entire header, including nano
     * * * If a user is not past the scroll threshold, keep the header fixed, but hide the nano
     * * On any upscroll...
     * * * If a user is past the scroll threshold, show the header, excluding the nano
     * * * If a user is not past the scroll threshold, show the entire header, including nano
     **/

    //Mobile scrolling
    if (getCurrentView() == 'sml' || getCurrentView() == 'med') {
      const st = window.scrollY || document.documentElement.scrollTop; // Credits: "https://github.com/qeremy/so/blob/master/so.dom.js#L426"
      if (st > this.lastScrollTop) {
        if ((document.body.scrollTop > 0 || document.documentElement.scrollTop > 0) && st < mobileScrollThreshold) {
          // Downscroll, past mobile scroll threshold
          document.querySelector('#shopify-section-header').style.top = `-${document.querySelector('nano-bar').offsetHeight || document.querySelector('.nano--container').offsetHeight}px`;
          if (document.querySelector('.js-nanobar.active')) document.querySelector('.js-nanobar.active').style.marginTop = `-1px`;
        } else {
          // Downscroll, not past mobile scroll threshold
          document.querySelector('#shopify-section-header').style.top = `-${document.querySelector('#shopify-section-header').offsetHeight}px`;
          if (document.querySelector('.js-nanobar.active')) document.querySelector('.js-nanobar.active').style.marginTop = `0`;
        }

        document.querySelector('.section-in-page-navigation')?.setAttribute('data-scroll-direction', 'down');
      } else if (st < this.lastScrollTop) {
        // Upscroll
        if (!document.body.getAttribute('data-scrollable') !== false) {
          document.querySelector('#shopify-section-header').style.top = `-${document.querySelector('nano-bar').offsetHeight || document.querySelector('.nano--container').offsetHeight}px`;
          document.querySelector('.section-in-page-navigation')?.setAttribute('data-scroll-direction', 'up');
        }
      }
      this.lastScrollTop = st <= 0 ? 0 : st;
    }
    //Desktop Scrolling: when you have scrolled any amount on a page, unfix the positioning of the nanobar at the very top, while keeping the header fixed
    else {
      if (document.body.scrollTop > 0 || document.documentElement.scrollTop > 0) {
        document.querySelector('#shopify-section-header').style.top = `-${document.querySelector('nano-bar').offsetHeight || document.querySelector('.nano--container').offsetHeight}px`;
        if (document.querySelector('.js-nanobar.active')) document.querySelector('.js-nanobar.active').style.marginTop = `-1px`;
      } else {
        document.querySelector('#shopify-section-header').style.top = '0';
        if (document.querySelector('.js-nanobar.active')) document.querySelector('.js-nanobar.active').style.marginTop = `0`;
      }
    }
  }

  //determines if user is currently on an L1 page and adds active state. Desktop only
  checkActiveL1() {
    document.querySelectorAll('header [data-ht-element="nav-l1-link"]').forEach((L1) => {
      const navHref = L1.getAttribute('href');
      if (window.location.href.includes(navHref) && navHref != '#') {
        L1.classList.add('current--page');
      }
    });
  }

  setTabNavBehavior(navType = 'drawer') {
    switch (navType) {
      case 'topnav':
        const l1s = document.querySelectorAll('[data-ht-element="nav-l1-link"]');
        // On every L1 in the nav...
        l1s.forEach((l1, i) => {
          l1.addEventListener('keydown', (e: KeyboardEvent) => {
            // ... with the L1 nav item focused, when Tab is pressed...
            if (e.key === 'Tab' && !e.shiftKey) {
              // ... move focus to first L2 nav item if top drawer is open...
              const topDrawerList = document.querySelector('.top-drawer.active div.active');
              if (topDrawerList) {
                e.preventDefault();
                const first = getFirstFocusableElement(topDrawerList);
                const last = getLastFocusableElement(topDrawerList);
                first.focus();

                /*
                 * On focus of last L3 item in a nav manu, move focus to the next
                 * L1 item when tab is pressed.
                 */
                last.addEventListener('keydown', (e: KeyboardEvent) => {
                  if (e.key === 'Tab' && !e.shiftKey) {
                    e.preventDefault;
                    const nextL1 = l1s[i] as HTMLElement;
                    if (nextL1) {
                      nextL1.focus();
                    }
                  }
                });
              }
            }
          });
        });

        /*
         * Ensure the last nav icon goes to main page content
         * I couldn't use getFirstFocusableElement and getLastFocusableElement on
         * .header__icon bc of the way the markup and styles are set up for the
         * email nudge icon. Something to consider when we build stuff like this
         * in the future.
         */
        const navIcons = document.querySelectorAll('.header__icons button');
        const firstIcon = navIcons[1];
        const lastIcon = navIcons[navIcons.length - 1];
        const firstMainFocusable = getFirstFocusableElement(document.querySelector('main'));
        const exposedNav = document.querySelector('.exposed-nav') as HTMLElement;
        const exposedNavLink = exposedNav ? getFirstFocusableElement(exposedNav) : null;

        if (firstIcon) {
          firstIcon.addEventListener('focus', () => {
            const topNavDrawer = document.querySelector('.top-drawer');
            const topNavDrawerModal = document.querySelector('.navigation-overlay');
            topNavDrawer.classList.remove('active');
            topNavDrawerModal.classList.remove('active');
          });
        }

        if (lastIcon) {
          lastIcon.addEventListener('keydown', (e: KeyboardEvent) => {
            if (e.key === 'Tab' && !e.shiftKey) {
              if (!exposedNavLink || exposedNav.style.display === 'none') {
                firstMainFocusable.focus();
              } else {
                exposedNavLink.focus();
              }
            }
          });
        }
    }
  }

  //fires function to build nav elements, fires functions to initiate event listeners on built elements
  async loadHeader() {
    await this.buildElements();
    this.initBuiltElementListeners();
    this.checkActiveL1();
    window.Brooklinen.navLoaded = true;
  }
}
